/**
 * Contacts surface (live) - Upload contacts flow. Four steps:
 *   1. CSV/XLSX select (drag-and-drop or picker). The file is parsed client-side
 *      (csv-parse.ts, with P10 header normalization) to drive the tag-picker.
 *   2. Owner + source group. Admins can choose an active consultant; the server
 *      records that consultant as owner and the signed-in admin as uploader.
 *   3. Refine by seniority + function (optional), with an all-vs-matching mode
 *      and a title-exclusion keyword. The selection is sent as filters{} and
 *      applied server-side; the client preview count is an estimate.
 *   4. Review + import. The full CSV + controls go to import_contacts_csv in
 *      preview mode; the server classifies each row (new / duplicate / error)
 *      and applies the filters, and that plan drives the row-level table. Import
 *      commits the same CSV + controls.
 * Import executes the real write path; no rows are fabricated. If the file has
 * no recognizable columns, the tag-picker is skipped and the whole file is
 * imported server-side.
 */

import { useState } from "react";
import type { ImportFilters, ImportOptions, ImportPreview } from "../types";
import type { AssignableUser } from "@/features/signals/types";
import type { FunctionArea, Seniority } from "../derive";
import { parseCsv, type ParsedContact } from "../csv-parse";
import { readContactFile } from "../contact-file";
import { rowMatches } from "../upload-sample";
import { IconClose, IconUpload } from "../contact-icons";

interface Parsed {
  valid: ParsedContact[];
  total: number;
  skipped: number;
  recognized: boolean;
  headerLine: string;
  text: string;
  fileName: string;
  convertedFromExcel: boolean;
}

const SENIORITY_TAGS: Seniority[] = ["C-Suite", "VP", "Director", "Manager"];
const FUNCTION_TAGS: FunctionArea[] = [
  "Technology",
  "Finance",
  "Operations",
  "Strategy",
  "HR",
  "Legal",
  "Supply Chain",
  "Marketing",
  "Sales",
];

const STEP_TITLES = [
  "Select a file",
  "Choose owner and source",
  "Refine by seniority and function (optional)",
  "Review and import",
] as const;

const STATUS_LABEL: Record<ImportPreview["rows"][number]["status"], string> = {
  valid: "New",
  duplicate: "Duplicate",
  excluded: "Excluded",
  error: "Error",
};

export function UploadCsvModal({
  preview,
  commit,
  sourceGroups,
  isAdmin,
  viewer,
  consultants,
  consultantsLoading,
  onDone,
  onClose,
}: {
  preview: (file: File, options: ImportOptions) => Promise<ImportPreview>;
  commit: (preview: ImportPreview) => Promise<{ imported: number }>;
  sourceGroups: string[];
  isAdmin: boolean;
  viewer: AssignableUser;
  consultants: AssignableUser[];
  consultantsLoading: boolean;
  onDone: (imported: number) => void;
  onClose: () => void;
}): JSX.Element {
  const [step, setStep] = useState(1);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [dragging, setDragging] = useState(false);
  const [sourceGroup, setSourceGroup] = useState("");
  const [ownerUserId, setOwnerUserId] = useState(viewer.id);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [seniority, setSeniority] = useState<Seniority[]>([]);
  const [functions, setFunctions] = useState<FunctionArea[]>([]);
  const [mode, setMode] = useState<"all" | "matched">("all");
  const [excludeKeyword, setExcludeKeyword] = useState("");
  const [serverPreview, setServerPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const intake = async (file: File) => {
    setError(null);
    setServerPreview(null);
    try {
      const contactFile = await readContactFile(file);
      const r = parseCsv(contactFile.text);
      setParsed({
        valid: r.valid,
        total: r.total,
        skipped: r.skipped,
        recognized: r.recognized,
        headerLine: r.headerLine,
        text: contactFile.text,
        fileName: contactFile.fileName,
        convertedFromExcel: contactFile.convertedFromExcel,
      });
    } catch (err: unknown) {
      setParsed(null);
      setError(err instanceof Error ? err.message : "Could not read the file.");
    }
  };

  const anyPills = seniority.length > 0 || functions.length > 0;
  const canRefine = !!parsed && parsed.recognized;
  const matchCount = parsed
    ? parsed.valid.filter((r) => rowMatches(r.title, seniority, functions)).length
    : 0;

  /** The controls sent to the server: the source group, plus the row filters
   *  (seniority/function only when "match" mode is chosen; keywords always). */
  const buildOptions = (): ImportOptions => {
    const matching = anyPills && mode === "matched";
    const filters: ImportFilters = {
      seniority: matching ? seniority : [],
      functions: matching ? functions : [],
      excludeKeywords: excludeKeyword.trim()
        ? excludeKeyword
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      excludeRowNumbers: [],
    };
    return {
      sourceGroup: sourceGroup.trim() ? sourceGroup.trim() : null,
      ownerUserId: isAdmin && ownerUserId !== viewer.id ? ownerUserId : null,
      filters,
    };
  };

  const directory = [viewer, ...consultants.filter((person) => person.id !== viewer.id)];
  const ownerNeedle = ownerSearch.trim().toLowerCase();
  const matchingOwners = directory.filter((person) =>
    [person.name, person.email, person.title ?? "", person.practice ?? ""]
      .join(" ")
      .toLowerCase()
      .includes(ownerNeedle)
  );
  const selectedOwner = directory.find((person) => person.id === ownerUserId) ?? viewer;

  const toggleSeniority = (s: Seniority) =>
    setSeniority((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const toggleFunction = (f: FunctionArea) =>
    setFunctions((p) => (p.includes(f) ? p.filter((x) => x !== f) : [...p, f]));

  const runPreview = () => {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    const file = new File([parsed.text], parsed.fileName, { type: "text/csv" });
    preview(file, buildOptions()).then(
      (result) => {
        setServerPreview(result);
        setBusy(false);
        setStep(4);
      },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof Error ? err.message : "Could not preview the import.");
      }
    );
  };

  const runCommit = () => {
    if (!serverPreview) return;
    setBusy(true);
    setError(null);
    commit(serverPreview).then(
      (res) => {
        setBusy(false);
        onDone(res.imported);
      },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof Error ? err.message : "Could not import the contacts.");
      }
    );
  };

  const toggleReviewRow = (line: number) => {
    setServerPreview((current) => {
      if (!current) return current;
      const rows = current.rows.map((row) => {
        if (row.line !== line) return row;
        if (row.status === "valid") {
          return { ...row, status: "excluded" as const, message: "Excluded from this upload" };
        }
        if (row.status === "excluded" && row.message === "Excluded from this upload") {
          return { ...row, status: "valid" as const, message: "" };
        }
        return row;
      });
      const excludeRowNumbers = rows
        .filter((row) => row.status === "excluded" && row.message === "Excluded from this upload")
        .map((row) => row.line);
      return {
        ...current,
        rows,
        options: {
          ...current.options,
          filters: { ...current.options.filters, excludeRowNumbers },
        },
        summary: {
          total: rows.length,
          valid: rows.filter((row) => row.status === "valid").length,
          duplicate: rows.filter((row) => row.status === "duplicate").length,
          excluded: rows.filter((row) => row.status === "excluded").length,
          error: rows.filter((row) => row.status === "error").length,
        },
      };
    });
  };

  const canContinue = (step === 1 && !!parsed) || step === 2 || step === 3;

  return (
    <div className="sxc-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="sxc-modal sxc-modal--upload"
        role="dialog"
        aria-modal="true"
        aria-label="Upload contacts"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="sxc-modal-close" aria-label="Close" onClick={onClose}>
          <IconClose />
        </button>

        <p className="sx-eyebrow">
          Step {step} of 4 ·{" "}
          {isAdmin ? `Uploading for ${selectedOwner.name}` : "Uploading to your contacts"}
        </p>
        <h2 className="sxc-form-title">{STEP_TITLES[step - 1]}</h2>

        <div className="sxc-steps-dots" aria-hidden="true">
          {[1, 2, 3, 4].map((n) => (
            <span key={n} className={`sxc-step-dot${n <= step ? " sxc-step-dot--on" : ""}`} />
          ))}
        </div>

        {step === 1 ? (
          <div className="sxc-upload-body">
            <label
              className={`sxc-upload-drop${dragging ? " sxc-upload-drop--drag" : ""}`}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file) void intake(file);
              }}
            >
              <IconUpload size={22} />
              <span className="sxc-upload-drop-main">
                {parsed?.fileName ?? "Drag a CSV or Excel file here, or click to choose one"}
              </span>
              <span className="sxc-upload-drop-sub">
                Names are safely normalized without removing accents. Title filters and duplicate
                checks run before anything is imported.
              </span>
              <input
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="sxc-sr-file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void intake(file);
                }}
              />
            </label>
            {parsed ? (
              parsed.recognized ? (
                <p className="sxc-upload-parsed">
                  <strong>{parsed.fileName}</strong>: {parsed.total} rows parsed
                  {parsed.convertedFromExcel ? " from the first Excel sheet" : ""}
                  {parsed.skipped > 0
                    ? `, ${parsed.skipped} skipped (missing name or company)`
                    : ""}
                  .
                </p>
              ) : (
                <p className="sxc-upload-parsed">
                  <strong>{parsed.fileName}</strong> selected. Contact columns were not recognized
                  for filtering, so the whole file will be imported and classified server-side.
                </p>
              )
            ) : null}
            {error ? <p className="sxc-upload-error">{error}</p> : null}
          </div>
        ) : null}

        {step === 2 ? (
          <div className="sxc-upload-body">
            {isAdmin ? (
              <div className="sxc-owner-picker">
                <div>
                  <p className="sx-eyebrow">Contact owner</p>
                  <p className="sxc-helper">
                    Choose whose contact book receives this upload. You remain recorded as the
                    uploader; the consultant remains the owner.
                  </p>
                </div>
                <input
                  className="sx-field"
                  type="search"
                  placeholder="Search by name, email, title, or practice"
                  value={ownerSearch}
                  onChange={(event) => setOwnerSearch(event.target.value)}
                />
                <div className="sxc-owner-list" role="listbox" aria-label="Contact owner">
                  {consultantsLoading ? (
                    <p className="sxc-helper">Loading consultants...</p>
                  ) : matchingOwners.length === 0 ? (
                    <p className="sxc-helper">No active consultant matches that search.</p>
                  ) : (
                    matchingOwners.slice(0, 30).map((person) => (
                      <button
                        key={person.id}
                        type="button"
                        role="option"
                        aria-selected={person.id === ownerUserId}
                        className={`sxc-owner-option${person.id === ownerUserId ? " sxc-owner-option--selected" : ""}`}
                        onClick={() => setOwnerUserId(person.id)}
                      >
                        <span>
                          <strong>
                            {person.id === viewer.id ? `${person.name} (you)` : person.name}
                          </strong>
                          <small>{person.email}</small>
                        </span>
                        <span className="sxc-owner-meta">
                          {[person.title, person.practice].filter(Boolean).join(" · ") ||
                            "Consultant"}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <p className="sxc-helper">This upload will be added to your contact book.</p>
            )}
            <label className="sxc-field-wrap">
              <span className="sx-eyebrow">Source group (optional)</span>
              <input
                className="sx-field"
                list="sxc-upload-source-groups"
                placeholder="e.g. Web Summit 2025"
                value={sourceGroup}
                onChange={(e) => setSourceGroup(e.target.value)}
              />
              <datalist id="sxc-upload-source-groups">
                {sourceGroups.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </label>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="sxc-upload-body">
            {canRefine ? (
              <>
                <div className="sxc-tagpick" role="group" aria-label="Seniority levels">
                  <p className="sxc-tagpick-q">What seniority levels interest you?</p>
                  <div className="sx-pill-row">
                    {SENIORITY_TAGS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="sx-typepill"
                        role="checkbox"
                        aria-checked={seniority.includes(s)}
                        onClick={() => toggleSeniority(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sxc-tagpick" role="group" aria-label="Functions">
                  <p className="sxc-tagpick-q">What functions interest you?</p>
                  <div className="sx-pill-row">
                    {FUNCTION_TAGS.map((f) => (
                      <button
                        key={f}
                        type="button"
                        className="sx-typepill"
                        role="checkbox"
                        aria-checked={functions.includes(f)}
                        onClick={() => toggleFunction(f)}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {anyPills ? (
                  <div className="sxc-tagpick" role="group" aria-label="Import mode">
                    <p className="sxc-tagpick-q">Which rows should be imported?</p>
                    <div className="sxc-group-modes">
                      <button
                        type="button"
                        className={`sxc-group-mode${mode === "all" ? " sxc-group-mode--on" : ""}`}
                        aria-pressed={mode === "all"}
                        onClick={() => setMode("all")}
                      >
                        Import all rows
                      </button>
                      <button
                        type="button"
                        className={`sxc-group-mode${mode === "matched" ? " sxc-group-mode--on" : ""}`}
                        aria-pressed={mode === "matched"}
                        onClick={() => setMode("matched")}
                      >
                        Import only matching rows
                      </button>
                    </div>
                  </div>
                ) : null}

                <p className="sxc-tag-preview">
                  {matchCount} of {parsed?.valid.length ?? 0} parsed contacts match your selection
                  (the server applies the final filter)
                </p>

                <div className="sxc-tagpick">
                  <label className="sx-eyebrow" htmlFor="sxc-exclude">
                    Also exclude titles containing
                  </label>
                  <input
                    id="sxc-exclude"
                    className="sx-field"
                    placeholder="e.g. sales, marketing"
                    value={excludeKeyword}
                    onChange={(e) => setExcludeKeyword(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <p className="sxc-helper">
                Contact columns were not recognized, so there is nothing to filter on. The whole
                file will be sent for import and classified server-side.
              </p>
            )}
          </div>
        ) : null}

        {step === 4 ? (
          <div className="sxc-upload-body">
            {serverPreview ? (
              <>
                <div className="sxc-import-chips">
                  <span className="sxc-imp-chip">{serverPreview.summary.total} parsed</span>
                  <span className="sxc-imp-chip sxc-imp-chip--valid">
                    {serverPreview.summary.valid} new
                  </span>
                  <span className="sxc-imp-chip sxc-imp-chip--duplicate">
                    {serverPreview.summary.duplicate} duplicate
                  </span>
                  <span className="sxc-imp-chip sxc-imp-chip--excluded">
                    {serverPreview.summary.excluded} excluded
                  </span>
                  <span className="sxc-imp-chip sxc-imp-chip--error">
                    {serverPreview.summary.error} error
                  </span>
                </div>
                <div className="sxc-import-table-wrap">
                  <table className="sxc-import-table">
                    <thead>
                      <tr>
                        <th>Include</th>
                        <th>Row</th>
                        <th>Status</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Email</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serverPreview.rows.map((r) => (
                        <tr key={r.line} className={`sxc-imp-row sxc-imp-row--${r.status}`}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Include row ${r.line}`}
                              checked={r.status === "valid"}
                              disabled={
                                r.status !== "valid" &&
                                !(
                                  r.status === "excluded" &&
                                  r.message === "Excluded from this upload"
                                )
                              }
                              onChange={() => toggleReviewRow(r.line)}
                            />
                          </td>
                          <td className="sxc-imp-num">{r.line}</td>
                          <td>
                            <span className={`sxc-imp-badge sxc-imp-badge--${r.status}`}>
                              {STATUS_LABEL[r.status]}
                            </span>
                          </td>
                          <td>{r.input.name || "-"}</td>
                          <td>{r.input.company || "-"}</td>
                          <td>{r.input.email || "-"}</td>
                          <td className="sxc-imp-msg">{r.message || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="sxc-helper">
                  {serverPreview.summary.valid} of {serverPreview.summary.total} will be imported
                  for <strong>{selectedOwner.name}</strong>. Uncheck any new row to leave it out;
                  title exclusions, duplicates, and invalid rows are already separated.
                </p>
              </>
            ) : (
              <p className="sxc-helper">Building the import preview...</p>
            )}
            {error ? <p className="sxc-upload-error">{error}</p> : null}
          </div>
        ) : null}

        <div className="sxc-form-foot">
          {step > 1 ? (
            <button
              type="button"
              className="sx-btn sx-btn-p3"
              disabled={busy}
              onClick={() => setStep((s) => s - 1)}
            >
              Back
            </button>
          ) : (
            <button type="button" className="sx-btn sx-btn-p3" onClick={onClose}>
              Cancel
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              className="sx-cta"
              disabled={!canContinue}
              onClick={() => setStep((s) => s + 1)}
            >
              Continue
            </button>
          ) : step === 3 ? (
            <button
              type="button"
              className="sx-cta"
              disabled={busy || !parsed}
              onClick={runPreview}
            >
              {busy ? "Preparing preview..." : "Preview import"}
            </button>
          ) : (
            <button
              type="button"
              className="sx-cta"
              disabled={busy || !serverPreview || serverPreview.summary.valid === 0}
              onClick={runCommit}
            >
              {busy ? "Importing..." : `Import ${serverPreview?.summary.valid ?? 0} contacts`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
